import React, { useState, useCallback, useMemo } from 'react';
import { AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { sanitizeColumnName, escapeStringLiteral } from './utils/sqlGenerator';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ReturnType = 'VARCHAR' | 'INTEGER' | 'BIGINT' | 'DOUBLE' | 'BOOLEAN' | 'TIMESTAMP';

export const VALID_RETURN_TYPES: Set<string> = new Set([
  'VARCHAR', 'INTEGER', 'BIGINT', 'DOUBLE', 'BOOLEAN', 'TIMESTAMP',
]);

export const MAX_FIELDS = 100;

const INT32_MIN = -2147483648;
const INT32_MAX = 2147483647;

export interface DecomposedColumn {
  key: string;
  alias: string;
  selected: boolean;
  sampleValue: string;
  returnType: ReturnType;
}

export interface BatchJsonDecomposeProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceColumn: string;
  availableColumns?: string[];  // For column selector when sourceColumn is empty
  existingExtractions?: Array<{
    key: string;
    alias: string;
    returnType: string;
  }>;
  onApply: (
    columns: Array<{
      source_column: string;
      column_alias: string;
      transform_expression: string;
      column_order: number;
    }>
  ) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function toSnakeCase(key: string, index: number = 0): string {
  const result = key
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/[\s\-]+/g, '_')
    .replace(/[^a-zA-Z0-9_]/g, '')
    .toLowerCase();
  return result || `field_${index}`;
}

const ISO_DATE_PATTERN =
  /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})?)?$/;

export function inferReturnType(value: unknown): ReturnType {
  if (value === null || value === undefined) return 'VARCHAR';

  // Nested objects/arrays default to VARCHAR (TASK-006)
  if (typeof value === 'object') return 'VARCHAR';

  if (typeof value === 'boolean') return 'BOOLEAN';
  if (typeof value === 'number') {
    if (!Number.isInteger(value)) return 'DOUBLE';
    return (value >= INT32_MIN && value <= INT32_MAX) ? 'INTEGER' : 'BIGINT';
  }

  const str = String(value);

  if (str === 'true' || str === 'false') return 'BOOLEAN';

  if (ISO_DATE_PATTERN.test(str)) return 'TIMESTAMP';

  // Only attempt numeric detection on strings that contain no letters
  if (/^-?\d+$/.test(str)) {
    const num = Number(str);
    if (!isNaN(num) && num >= INT32_MIN && num <= INT32_MAX) return 'INTEGER';
    return 'BIGINT';
  }
  if (/^-?\d+\.\d+$/.test(str)) return 'DOUBLE';

  return 'VARCHAR';
}

export function truncate(value: string, max: number): string {
  if (value.length <= max) return value;
  return value.slice(0, max) + '\u2026';
}

export function buildTransformExpression(
  sourceColumn: string,
  key: string,
  returnType: string
): string {
  if (!VALID_RETURN_TYPES.has(returnType)) {
    throw new Error(`Invalid return type: "${returnType}". Allowed types: ${[...VALID_RETURN_TYPES].join(', ')}`);
  }
  const col = sanitizeColumnName(sourceColumn);
  const escapedKey = escapeStringLiteral(key);
  const extraction = `${col}->>'${escapedKey}'`;

  if (returnType !== 'VARCHAR') {
    return `TRY_CAST(${extraction} AS ${returnType})`;
  }
  return extraction;
}

function buildColumnsFromExisting(
  extractions: NonNullable<BatchJsonDecomposeProps['existingExtractions']>
): DecomposedColumn[] {
  return extractions.map((ext) => ({
    key: ext.key,
    alias: ext.alias,
    selected: true,
    sampleValue: '',
    returnType: (
      VALID_RETURN_TYPES.has(ext.returnType)
        ? ext.returnType
        : 'VARCHAR'
    ) as ReturnType,
  }));
}

const RETURN_TYPE_OPTIONS: { value: ReturnType; label: string }[] = [
  { value: 'VARCHAR', label: 'VARCHAR' },
  { value: 'INTEGER', label: 'INTEGER' },
  { value: 'BIGINT', label: 'BIGINT' },
  { value: 'DOUBLE', label: 'DOUBLE' },
  { value: 'BOOLEAN', label: 'BOOLEAN' },
  { value: 'TIMESTAMP', label: 'TIMESTAMP' },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const BatchJsonDecompose: React.FC<BatchJsonDecomposeProps> = ({
  open,
  onOpenChange,
  sourceColumn: sourceColumnProp,
  availableColumns,
  existingExtractions,
  onApply,
}) => {
  const [selectedColumn, setSelectedColumn] = useState(sourceColumnProp || '');
  const sourceColumn = sourceColumnProp || selectedColumn;
  const [jsonInput, setJsonInput] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [fields, setFields] = useState<DecomposedColumn[]>(() =>
    existingExtractions?.length ? buildColumnsFromExisting(existingExtractions) : []
  );

  // ---- Parse logic --------------------------------------------------------

  const handleParse = useCallback(() => {
    setParseError(null);

    const trimmed = jsonInput.trim();
    if (!trimmed) {
      setParseError('Please paste a JSON object before parsing.');
      return;
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      setParseError('Invalid JSON. Please check the syntax and try again.');
      return;
    }

    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      setParseError(
        'Expected a JSON object (e.g. {"key": "value"}). Arrays and primitives are not supported.'
      );
      return;
    }

    const record = parsed as Record<string, unknown>;
    const keys = Object.keys(record);

    if (keys.length === 0) {
      setParseError('The JSON object has no keys.');
      return;
    }

    if (keys.length > MAX_FIELDS) {
      setParseError(
        `JSON object has ${keys.length} keys, which exceeds the maximum of ${MAX_FIELDS}. ` +
        'Please reduce the number of fields or extract them in batches.'
      );
      return;
    }

    // Build lookup of existing selections so we preserve user edits
    const existingByKey = new Map<string, DecomposedColumn>();
    for (const f of fields) {
      existingByKey.set(f.key, f);
    }

    const newFields: DecomposedColumn[] = keys.map((key, index) => {
      const rawValue = record[key];
      const isNested = typeof rawValue === 'object' && rawValue !== null;
      const sampleValue =
        rawValue === null
          ? 'null'
          : isNested
            ? JSON.stringify(rawValue)
            : String(rawValue);

      const existing = existingByKey.get(key);
      return {
        key,
        alias: existing?.alias ?? toSnakeCase(key, index),
        selected: existing?.selected ?? true,
        sampleValue,
        returnType: existing?.returnType ?? (isNested ? 'VARCHAR' as ReturnType : inferReturnType(rawValue)),
      };
    });

    setFields(newFields);
  }, [jsonInput, fields]);

  // ---- Field mutation helpers ---------------------------------------------

  const updateField = useCallback(
    (index: number, patch: Partial<DecomposedColumn>) => {
      setFields((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], ...patch };
        return next;
      });
    },
    []
  );

  const selectAll = useCallback(() => {
    setFields((prev) => prev.map((f) => ({ ...f, selected: true })));
  }, []);

  const deselectAll = useCallback(() => {
    setFields((prev) => prev.map((f) => ({ ...f, selected: false })));
  }, []);

  // ---- Derived values -----------------------------------------------------

  const selectedCount = useMemo(
    () => fields.filter((f) => f.selected).length,
    [fields]
  );

  // Detect duplicate aliases among selected fields (case-insensitive)
  const duplicateAliases = useMemo(() => {
    const selected = fields.filter((f) => f.selected);
    const counts = new Map<string, number>();
    for (const f of selected) {
      const lower = f.alias.toLowerCase();
      counts.set(lower, (counts.get(lower) || 0) + 1);
    }
    const dupes = new Set<string>();
    for (const [lower, count] of counts) {
      if (count > 1) dupes.add(lower);
    }
    return dupes;
  }, [fields]);

  const hasDuplicateAliases = duplicateAliases.size > 0;

  const hasFields = fields.length > 0;

  // ---- Apply handler ------------------------------------------------------

  const handleApply = useCallback(() => {
    const selected = fields.filter((f) => f.selected);
    const result = selected.map((field, idx) => ({
      source_column: sourceColumn,
      column_alias: field.alias,
      transform_expression: buildTransformExpression(
        sourceColumn,
        field.key,
        field.returnType
      ),
      column_order: idx,
    }));
    onApply(result);
    onOpenChange(false);
  }, [fields, sourceColumn, onApply, onOpenChange]);

  // ---- Render -------------------------------------------------------------

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        {/* Header */}
        <DialogHeader>
          <DialogTitle>Batch JSON Decomposition</DialogTitle>
          <DialogDescription>
            Extract multiple fields from{' '}
            <code className="rounded bg-muted px-1 py-0.5 font-mono text-sm">
              {sourceColumn}
            </code>{' '}
            into separate view columns
          </DialogDescription>
        </DialogHeader>

        {/* Body */}
        <div className="flex-1 overflow-y-auto space-y-6 pr-1">
          {/* Column Selector (shown when no sourceColumn was pre-selected) */}
          {!sourceColumnProp && availableColumns && availableColumns.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="batch-column-select">Source Column</Label>
              <Select value={selectedColumn} onValueChange={setSelectedColumn}>
                <SelectTrigger id="batch-column-select">
                  <SelectValue placeholder="Select a JSON column to decompose" />
                </SelectTrigger>
                <SelectContent>
                  {availableColumns.map((col) => (
                    <SelectItem key={col} value={col}>
                      {col}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* JSON Sample Input */}
          <div className="space-y-2">
            <Label htmlFor="batch-json-input">
              {existingExtractions?.length
                ? 'Re-parse JSON (paste a new sample to update fields)'
                : 'Paste a sample JSON value'}
            </Label>
            <Textarea
              id="batch-json-input"
              rows={6}
              className="font-mono text-sm"
              placeholder='{"firstName": "Jane", "age": 32, "active": true}'
              value={jsonInput}
              onChange={(e) => {
                setJsonInput(e.target.value);
                if (parseError) setParseError(null);
              }}
            />
            <Button
              variant="default"
              size="sm"
              onClick={handleParse}
              disabled={!jsonInput.trim()}
            >
              Parse JSON
            </Button>

            {parseError && (
              <Alert variant="destructive" className="mt-2">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{parseError}</AlertDescription>
              </Alert>
            )}
          </div>

          {/* Extracted Fields Table */}
          {hasFields && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Extracted Fields</span>
                  <Badge variant="secondary">
                    {fields.length} field{fields.length !== 1 ? 's' : ''} found
                  </Badge>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={selectAll}>
                    Select All
                  </Button>
                  <Button variant="outline" size="sm" onClick={deselectAll}>
                    Deselect All
                  </Button>
                </div>
              </div>

              <div className="overflow-auto max-h-[400px] rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10" />
                      <TableHead className="min-w-[140px]">Key</TableHead>
                      <TableHead className="min-w-[120px]">
                        Sample Value
                      </TableHead>
                      <TableHead className="min-w-[160px]">Alias</TableHead>
                      <TableHead className="min-w-[140px]">Type</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fields.map((field, idx) => (
                      <TableRow key={field.key}>
                        <TableCell>
                          <Checkbox
                            checked={field.selected}
                            onCheckedChange={(checked) =>
                              updateField(idx, { selected: !!checked })
                            }
                            aria-label={`Select ${field.key}`}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {field.key}
                        </TableCell>
                        <TableCell
                          className="text-sm text-muted-foreground max-w-[200px] truncate"
                          title={field.sampleValue}
                        >
                          {truncate(field.sampleValue, 50)}
                        </TableCell>
                        <TableCell>
                          <Input
                            value={field.alias}
                            onChange={(e) =>
                              updateField(idx, { alias: e.target.value })
                            }
                            className={`h-8 text-sm ${
                              field.selected && duplicateAliases.has(field.alias.toLowerCase())
                                ? 'border-destructive'
                                : ''
                            }`}
                            aria-label={`Alias for ${field.key}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Select
                            value={field.returnType}
                            onValueChange={(v) =>
                              updateField(idx, {
                                returnType: v as ReturnType,
                              })
                            }
                          >
                            <SelectTrigger
                              className="h-8 text-sm"
                              aria-label={`Type for ${field.key}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {RETURN_TYPE_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {hasDuplicateAliases && (
                <Alert variant="destructive" className="mt-2">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Duplicate aliases detected. Each column must have a unique alias.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="default"
            disabled={selectedCount === 0 || !sourceColumn || hasDuplicateAliases}
            onClick={handleApply}
          >
            Add {selectedCount} Column{selectedCount !== 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
